import { useMutation, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { RetrievalPageControls } from "./RetrievalPageControls";
import { Download, Eye, FileText, FileUp, X } from "lucide-react";
import { useState } from "react";
import type { ReactNode } from "react";

import type { ArtifactMetadata, ArtifactScope } from "../../shared/customer-context-schemas";
import type { Artifact } from "../../shared/customer-context-schemas";
import { Button, buttonVariants } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { FieldError } from "../components/ui/field";
import {
  artifactOriginalHref,
  artifactPreviewHref,
  listArtifacts,
  prepareArtifactUpload,
  uploadArtifact,
} from "./api";

const PREVIEW_MEDIA_TYPES = new Set([
  "application/json",
  "application/pdf",
  "application/xml",
  "image/avif",
  "image/gif",
  "image/jpeg",
  "image/png",
  "image/webp",
  "message/rfc822",
]);

function canPreviewArtifact(mediaType: string): boolean {
  const normalized = mediaType.split(";", 1)[0]?.trim().toLowerCase() ?? "";
  return normalized.startsWith("text/") || PREVIEW_MEDIA_TYPES.has(normalized);
}

function emailAddresses(value: string | undefined): string[] {
  if (!value) return [];
  return value
    .split(",")
    .map((part) => /<([^>]+)>/.exec(part)?.[1] ?? part.trim())
    .map((address) => address.trim().toLowerCase())
    .filter((address) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address));
}

async function emlHeaders(file: File): Promise<Map<string, string>> {
  const headerText = await file.slice(0, Math.min(file.size, 256 * 1024)).text();
  const headerBlock = headerText.split(/\r?\n\r?\n/, 1)[0] ?? "";
  const unfolded = headerBlock.replace(/\r?\n[\t ]+/g, " ");
  const headers = new Map<string, string>();
  for (const line of unfolded.split(/\r?\n/)) {
    const separator = line.indexOf(":");
    if (separator < 1) continue;
    headers.set(line.slice(0, separator).trim().toLowerCase(), line.slice(separator + 1).trim());
  }
  return headers;
}

async function emlMetadata(file: File, toolId: string | null): Promise<ArtifactMetadata> {
  const headers = await emlHeaders(file);
  const from = emailAddresses(headers.get("from"))[0];
  const to = emailAddresses(headers.get("to"));
  const sentAtMilliseconds = Date.parse(headers.get("date") ?? "");
  const subject = headers.get("subject")?.trim();
  if (!from || to.length === 0 || !Number.isFinite(sentAtMilliseconds) || !subject) {
    throw new Error("This .eml file is missing a valid From, To, Date, or Subject header.");
  }
  return {
    sourceType: "email",
    title: subject,
    filename: file.name,
    mediaType: "message/rfc822",
    byteCount: file.size,
    sourceCreatedAt: Math.floor(sentAtMilliseconds / 1000),
    sourceModifiedAt: file.lastModified ? Math.floor(file.lastModified / 1000) : null,
    sourceSystem: "eml-upload",
    sourceRecordId: headers.get("message-id") ?? null,
    toolId,
    email: {
      from,
      to,
      cc: emailAddresses(headers.get("cc")),
      bcc: emailAddresses(headers.get("bcc")),
      subject,
      messageId: headers.get("message-id") ?? null,
      inReplyTo: headers.get("in-reply-to") ?? null,
      references: (headers.get("references") ?? "").split(/\s+/).filter(Boolean),
      sentAt: Math.floor(sentAtMilliseconds / 1000),
    },
  };
}

function ArtifactRows({
  organizationId,
  artifacts,
}: {
  organizationId: string;
  artifacts: Artifact[];
}) {
  return (
    <div className="space-y-2">
      {artifacts.map((artifact) => (
        <div
          key={artifact.id}
          className="flex items-center justify-between gap-4 rounded-sm border p-3"
        >
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{artifact.title}</p>
            <p className="truncate text-xs text-muted-foreground">
              {artifact.filename} · {artifact.sourceType} · {artifact.searchStatus}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            {canPreviewArtifact(artifact.mediaType) ? (
              <a
                className={buttonVariants({
                  variant: "outline",
                  size: "sm",
                  className: "min-h-10",
                })}
                href={artifactPreviewHref(organizationId, artifact.id)}
                rel="noreferrer"
                target="_blank"
              >
                <Eye /> Preview
              </a>
            ) : null}
            <a
              className={buttonVariants({ variant: "outline", size: "sm", className: "min-h-10" })}
              href={artifactOriginalHref(organizationId, artifact.id)}
            >
              <Download /> Original
            </a>
          </div>
        </div>
      ))}
    </div>
  );
}

function fileKey(file: File): string {
  return [file.name, file.size, file.lastModified, file.type].join("\u0000");
}

function mergeFiles(current: File[], incoming: File[]): File[] {
  const files = new Map(current.map((file) => [fileKey(file), file]));
  for (const file of incoming) files.set(fileKey(file), file);
  return [...files.values()];
}

function formatBytes(value: number): string {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.ceil(value / 1024)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function UploadPicker({
  selectedFiles,
  setSelectedFiles,
}: {
  selectedFiles: File[];
  setSelectedFiles: (files: File[]) => void;
}) {
  const addFiles = (files: FileList | null) => {
    if (files) setSelectedFiles(mergeFiles(selectedFiles, Array.from(files)));
  };
  return (
    <label
      className="flex cursor-pointer flex-col items-center gap-2 rounded-sm border border-dashed p-7 text-center hover:border-primary"
      onDragOver={(event) => {
        event.preventDefault();
      }}
      onDrop={(event) => {
        event.preventDefault();
        addFiles(event.dataTransfer.files);
      }}
    >
      <FileUp className="size-5" />
      <span className="text-sm font-semibold">
        {selectedFiles.length > 0
          ? `${selectedFiles.length} ${selectedFiles.length === 1 ? "file" : "files"} selected`
          : "Drop files here or choose files"}
      </span>
      <span className="text-xs text-muted-foreground">Up to 100 MB per file</span>
      <input
        className="sr-only"
        type="file"
        multiple
        onChange={(event) => {
          addFiles(event.target.files);
          event.target.value = "";
        }}
      />
    </label>
  );
}

function SelectedFiles({
  disabled,
  failures,
  files,
  removeFile,
}: {
  disabled: boolean;
  failures: Map<string, string>;
  files: File[];
  removeFile: (file: File) => void;
}) {
  if (files.length === 0) return null;
  return (
    <ul className="space-y-2" aria-label="Files selected for upload">
      {files.map((file) => {
        const error = failures.get(fileKey(file));
        return (
          <li className="flex items-center gap-3 rounded-sm border p-3" key={fileKey(file)}>
            <FileText className="size-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{file.name}</p>
              <p className="text-xs text-muted-foreground">{formatBytes(file.size)}</p>
              {error ? <FieldError>{error}</FieldError> : null}
            </div>
            <Button
              aria-label={`Remove ${file.name}`}
              disabled={disabled}
              onClick={() => {
                removeFile(file);
              }}
              size="icon"
              type="button"
              variant="ghost"
            >
              <X />
            </Button>
          </li>
        );
      })}
    </ul>
  );
}

async function fileMetadata(file: File, toolId: string | null): Promise<ArtifactMetadata> {
  if (file.name.toLowerCase().endsWith(".eml")) return emlMetadata(file, toolId);
  const mediaType = file.type || "application/octet-stream";
  const sourceType = mediaType.startsWith("image/")
    ? "image"
    : mediaType.startsWith("video/")
      ? "video"
      : "file";
  return {
    sourceType,
    title: file.name.replace(/\.[^.]+$/, "") || file.name,
    filename: file.name,
    mediaType,
    byteCount: file.size,
    sourceCreatedAt: null,
    sourceModifiedAt: file.lastModified ? Math.floor(file.lastModified / 1000) : null,
    sourceSystem: "browser-upload",
    sourceRecordId: null,
    toolId,
  };
}

function useArtifactUploadBatch(organizationId: string, toolId: string | undefined) {
  const queryClient = useQueryClient();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadFailures, setUploadFailures] = useState<Map<string, string>>(new Map());
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(
    null,
  );
  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      const failed: { file: File; message: string }[] = [];
      let succeeded = 0;
      for (const [index, file] of files.entries()) {
        setUploadProgress({ current: index + 1, total: files.length });
        try {
          const reservation = await prepareArtifactUpload(
            organizationId,
            await fileMetadata(file, toolId ?? null),
          );
          await uploadArtifact(organizationId, reservation, file);
          succeeded += 1;
        } catch (error) {
          failed.push({
            file,
            message: error instanceof Error ? error.message : "This file could not be uploaded.",
          });
        }
      }
      return { failed, succeeded };
    },
    onSuccess: async ({ failed, succeeded }) => {
      setSelectedFiles(failed.map(({ file }) => file));
      setUploadFailures(new Map(failed.map(({ file, message }) => [fileKey(file), message])));
      if (succeeded > 0) {
        await queryClient.invalidateQueries({
          queryKey: ["ops", "customers", organizationId, "artifacts"],
        });
      }
    },
    onSettled: () => {
      setUploadProgress(null);
    },
  });
  const updateSelectedFiles = (files: File[]) => {
    setSelectedFiles(files);
    setUploadFailures(new Map());
  };
  return { selectedFiles, updateSelectedFiles, upload, uploadFailures, uploadProgress };
}

function ArtifactSection({
  children,
  embedded,
  toolName,
}: {
  children: ReactNode;
  embedded: boolean;
  toolName: string | undefined;
}) {
  if (embedded) {
    const titleId = toolName ? "tool-files-title" : "customer-files-title";
    return (
      <section className="space-y-4" aria-labelledby={titleId}>
        <div>
          <h3 id={titleId} className="text-base font-bold text-balance">
            Files
          </h3>
          <p className="mt-1 text-sm text-pretty text-muted-foreground">
            {toolName
              ? `Upload source material for ${toolName}. These files stay linked to this tool and are indexed for AI search when supported.`
              : "Upload source material that applies across this customer. Tool-specific files stay in their tool knowledge scope."}
          </p>
        </div>
        {children}
      </section>
    );
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>{toolName ? `${toolName} files` : "Customer-wide files"}</CardTitle>
        <CardDescription>
          {toolName
            ? `Files uploaded here are linked to ${toolName} and remain inside this customer's isolated context.`
            : "Files added here apply to the company as a whole. Tool-specific files stay on that tool's page and are excluded here."}{" "}
          Selected `.eml` files retain sender, recipients, subject, message identifiers, and sent
          time.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">{children}</CardContent>
    </Card>
  );
}

export function CustomerArtifacts({
  organizationId,
  ready,
  canRead,
  canUpload,
  embedded = false,
  toolId,
  toolName,
}: {
  organizationId: string;
  ready: boolean;
  canRead: boolean;
  canUpload: boolean;
  embedded?: boolean;
  toolId?: string;
  toolName?: string;
}) {
  const { selectedFiles, updateSelectedFiles, upload, uploadFailures, uploadProgress } =
    useArtifactUploadBatch(organizationId, toolId);
  const scope: ArtifactScope = toolId ? { kind: "tool", toolId } : { kind: "customer" };
  const artifacts = useInfiniteQuery({
    queryKey: ["ops", "customers", organizationId, "artifacts", scope.kind, toolId ?? null],
    queryFn: ({ pageParam }) => listArtifacts(organizationId, scope, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.page.nextCursor,
    enabled: ready && canRead,
    refetchInterval: (query) =>
      query.state.data?.pages.some((page) =>
        page.artifacts.some((artifact) => artifact.searchStatus === "pending"),
      )
        ? 2_000
        : false,
  });
  const emptyVisible = !artifacts.isPending && !artifacts.error;
  const content = (
    <CustomerArtifactContent
      artifacts={artifacts.data?.pages.flatMap((page) => page.artifacts) ?? []}
      artifactsError={artifacts.error}
      emptyVisible={emptyVisible}
      canRead={canRead}
      canUpload={canUpload}
      onUpload={() => {
        if (selectedFiles.length > 0) {
          updateSelectedFiles(selectedFiles);
          upload.mutate(selectedFiles);
        }
      }}
      organizationId={organizationId}
      ready={ready}
      selectedFiles={selectedFiles}
      setSelectedFiles={updateSelectedFiles}
      toolName={toolName}
      uploadFailures={uploadFailures}
      uploadPending={upload.isPending}
      uploadProgress={uploadProgress}
    />
  );
  return (
    <ArtifactSection embedded={embedded} toolName={toolName}>
      {content}
      {ready && canRead ? (
        <RetrievalPageControls
          label="files"
          error={null}
          pending={artifacts.isPending}
          fetching={artifacts.isFetchingNextPage}
          hasMore={artifacts.hasNextPage}
          loadMore={() => {
            void artifacts.fetchNextPage();
          }}
        />
      ) : null}
    </ArtifactSection>
  );
}

function CustomerArtifactContent({
  artifacts,
  artifactsError,
  emptyVisible,
  canRead,
  canUpload,
  onUpload,
  organizationId,
  ready,
  selectedFiles,
  setSelectedFiles,
  toolName,
  uploadFailures,
  uploadPending,
  uploadProgress,
}: {
  artifacts: Artifact[];
  artifactsError: Error | null;
  emptyVisible: boolean;
  canRead: boolean;
  canUpload: boolean;
  onUpload: () => void;
  organizationId: string;
  ready: boolean;
  selectedFiles: File[];
  setSelectedFiles: (files: File[]) => void;
  toolName: string | undefined;
  uploadFailures: Map<string, string>;
  uploadPending: boolean;
  uploadProgress: { current: number; total: number } | null;
}) {
  if (!ready) {
    return (
      <p className="rounded-sm border border-dashed p-4 text-sm text-muted-foreground">
        Uploads unlock after this customer's context resources finish provisioning.
      </p>
    );
  }
  if (!canRead) {
    return (
      <p className="rounded-sm border border-dashed p-4 text-sm text-muted-foreground">
        Your employee permissions do not include customer file access.
      </p>
    );
  }
  return (
    <>
      {canUpload ? (
        <UploadPicker selectedFiles={selectedFiles} setSelectedFiles={setSelectedFiles} />
      ) : null}
      <SelectedFiles
        disabled={uploadPending}
        failures={uploadFailures}
        files={selectedFiles}
        removeFile={(file) => {
          setSelectedFiles(selectedFiles.filter((selected) => fileKey(selected) !== fileKey(file)));
        }}
      />
      {selectedFiles.length > 0 && canUpload ? (
        <Button onClick={onUpload} disabled={uploadPending}>
          <FileUp />
          {uploadProgress
            ? `Uploading ${uploadProgress.current} of ${uploadProgress.total}…`
            : `Upload ${selectedFiles.length} ${selectedFiles.length === 1 ? "file" : "files"} to ${toolName ?? "this customer"}`}
        </Button>
      ) : null}
      {artifactsError ? <FieldError>{artifactsError.message}</FieldError> : null}
      {artifacts.length > 0 ? (
        <ArtifactRows organizationId={organizationId} artifacts={artifacts} />
      ) : emptyVisible ? (
        <EmptyArtifactList toolName={toolName} />
      ) : null}
    </>
  );
}

function EmptyArtifactList({ toolName }: { toolName: string | undefined }) {
  return (
    <p className="rounded-sm border border-dashed p-4 text-sm text-muted-foreground">
      {toolName ? `No files have been added to ${toolName} yet.` : "No customer-wide files yet."}
    </p>
  );
}
